import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { ProfileImg } from "../../assets/images";

const BASE_URL = process.env.REACT_APP_API_URL.replace(/\/v1\/$/, "");

const MyProfile = () => {
  const [profilePreview, setProfilePreview] = useState(null);
  const [teamCount, setTeamCount] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.access_level === 1;

  const headers = {
    "api-key": process.env.REACT_APP_API_KEY,
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}auth/user_details`,
          { headers }
        );
        if (res.data.status === "success") {
          setProfileData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch profile", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchTeamCount = async () => {
      if (!isAdmin) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}auth/list_users`,
          { headers }
        );
        if (res.data.status === "success") {
          setTeamCount(res.data.data?.pagination?.total ?? 0);
        }
      } catch {
        setTeamCount(0);
      }
    };

    fetchProfile();
    fetchTeamCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      name: profileData?.name || "",
      contact: profileData?.contact || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .matches(/^[A-Za-z\s]+$/, "Name must contain only letters"),
      contact: Yup.string()
        .optional()
        .max(20, "Contact number must not exceed 20 characters"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setError("");
      setSuccess("");
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}auth/edit_profile`,
          { name: values.name, contact: values.contact },
          { headers }
        );
        if (res.data.status === "success") {
          const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
          localStorage.setItem(
            "user",
            JSON.stringify({ ...storedUser, name: values.name })
          );
          setProfileData((prev) => ({ ...prev, ...values }));
          setSuccess("Profile updated successfully!");
        } else {
          setError(res.data.message?.keyword || "Failed to update profile.");
        }
      } catch (err) {
        setError(err.response?.data?.message?.keyword || "Something went wrong.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setProfilePreview(reader.result);
    reader.readAsDataURL(file);

    // Upload to server
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("avatar", file);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}auth/upload_avatar`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (res.data.status === "success") {
        const savedUrl = res.data.data?.profile_picture;
        setProfileData((prev) => ({ ...prev, profile_picture: savedUrl }));
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({ ...storedUser, profile_picture: savedUrl }));
        window.dispatchEvent(new Event("userProfileUpdated"));
      }
    } catch (err) {
      console.error("Avatar upload failed", err);
    } finally {
      setAvatarUploading(false);
      // Reset input so same file can be reselected
      e.target.value = "";
    }
  };

  const getAvatarSrc = () => {
    if (profilePreview) return profilePreview;
    if (profileData?.profile_picture) return `${BASE_URL}${profileData.profile_picture}`;
    return ProfileImg;
  };

  if (loading) {
    return (
      <div className="admin-page">
        <p className="text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="my-account-header">
        <h1 className="page-section-title">My Profile</h1>
        <p className="my-account-subtitle">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="my-account-form">
        {/* Profile photo */}
        <div className="profile-photo-wrapper">
          <div className="profile-photo-circle">
            <img
              src={getAvatarSrc()}
              alt="Profile"
              className="profile-photo-img"
            />
            {avatarUploading && (
              <div className="profile-photo-uploading">
                <span className="spinner-border spinner-border-sm" />
              </div>
            )}
            <button
              type="button"
              className="profile-photo-edit-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarUploading}
              title="Change profile photo"
            >
              <i className="icon-icon-8"></i>
            </button>
          </div>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="profile-name">Name of Admin</label>
                <input
                  id="profile-name"
                  type="text"
                  name="name"
                  className={`form-control ${
                    formik.touched.name && formik.errors.name ? "is-invalid" : ""
                  }`}
                  placeholder="Enter your name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="invalid-feedback d-block">{formik.errors.name}</div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="profile-email">Email Address</label>
                <input
                  id="profile-email"
                  type="email"
                  className="form-control"
                  value={profileData?.email || ""}
                  readOnly
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="profile-contact">Contact Number</label>
                <input
                  id="profile-contact"
                  type="text"
                  name="contact"
                  className={`form-control ${
                    formik.touched.contact && formik.errors.contact ? "is-invalid" : ""
                  }`}
                  placeholder="Enter contact number"
                  value={formik.values.contact}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.contact && formik.errors.contact && (
                  <div className="invalid-feedback d-block">{formik.errors.contact}</div>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="profile-team-count">No. of Assigned Team Members</label>
                  <input
                    id="profile-team-count"
                    type="text"
                    className="form-control"
                    value={teamCount ?? "--"}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-danger mb-3">{error}</p>}
          {success && <p className="text-success mb-3">{success}</p>}

          <button
            type="submit"
            className="btns my-account-save-btn"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Saving..." : "Save details"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MyProfile;
